// =====================================================================
//  midasReadiness.ts — MIDAS Data Readiness derivation util (DR-2)
//
//  SINGLE SOURCE OF TRUTH for every readiness surface (catalog cards +
//  banner + filter, machine profile card, machinery cards row, MIDAS
//  picker). NEVER inline-duplicate this derivation — import from here.
//
//  Contract + locked product decisions live in:
//    IMPLEMENTATION_TEMPLATES/ToDo/Machinery/MIDAS_DATA_READINESS_SPEC.md (§1, §2)
//    IMPLEMENTATION_TEMPLATES/ToDo/Machinery/MIDAS_DATA_READINESS_UI_SPEC.md (§1)
//
//  Locked rules encoded here:
//   - aiReady        = hasDirectDocs OR dynamicCapability === "proven".
//                      Fanout-only models (docs but no direct source, not
//                      proven) are NOT aiReady → NO banner, just a lit dot.
//   - banner tier    = OEM gold OUTRANKS AI blue; max ONE per card.
//   - midasDot lit   = any ingested doc (fanout-only still lights it).
//   - languages      = informational ONLY, never a gate. (Not derived here.)
//
//  LIVE dot — TWO functions, keep them distinct:
//   - liveDotState(readiness, ctx)  → SEMANTIC state for LOGIC consumers
//       ("connected" | "proven" | "possible" | null). Use when you need to
//       know *why* the dot is what it is.
//   - liveDotVisual(readiness, ctx) → RENDERING state for the DOT VISUAL
//       ("lit" | "hollow" | "unlit"). EVERY readiness surface (catalog cards,
//       machine profile, machinery cards row, MIDAS picker — DR-3/DR-4/DR-5)
//       MUST render the LIVE dot from THIS function so the same machine looks
//       identical everywhere. Do NOT re-derive the lit/hollow/unlit mapping
//       inline. Mapping: proven|connected → lit; possible OR (readiness
//       present but capability unknown) → hollow; no readiness → unlit.
// =====================================================================

export type DynamicCapability = "proven" | "possible" | null;
export type LiveDotState = "connected" | "proven" | "possible" | null;
export type LiveDotVisual = "lit" | "hollow" | "unlit";
export type BannerTier = "oem-gold" | "ai-blue" | null;

/**
 * Per-doc-type distinct source-document counts (closed enum).
 *
 * `oemNodeDocs` (DR-6, contract amendment 2026-08-18) is the FIFTH value —
 * additive inside `schema_version: 1`, emitted only for node-scoped OEM doc
 * bundles (node analysis + PMC ladder + schematic sheets + OEM alarm texts +
 * panel description shipped as one package). Its count is the number of
 * DISTINCT source documents in that package, same unit as the other four.
 */
export interface ReadinessDocCounts {
  manual?: number;
  electrical?: number;
  alarms?: number;
  specSheet?: number;
  oemNodeDocs?: number;
}

/**
 * Readiness VM as it rides on `CatalogModelListItem.readiness` (backend
 * lands in parallel — this mirrors MIDAS_DATA_READINESS_SPEC.md §5.1). The
 * "has a direct source" flag has drifted names across the spec/DB/VM
 * (`hasDirectDocs` vs `hasDirectSource`); we read either, and fall back to
 * a non-empty `directSources[]`, so the util is correct whichever the API
 * ultimately serializes.
 */
export interface ReadinessVM {
  docCounts?: ReadinessDocCounts | null;
  totalDocs?: number | null;
  docTypes?: string[] | null;
  languages?: string[] | null;
  hasDirectDocs?: boolean | null;
  hasDirectSource?: boolean | null;
  directSources?: string[] | null;
  brandFanoutSources?: string[] | null;
  dynamicCapability?: DynamicCapability;
  lastIngested?: string | null;
}

/** The subset of a catalog model the derivation needs. */
export interface ReadinessModel {
  readiness?: ReadinessVM | null;
  isOemPartner?: boolean;
  oemPartnerName?: string | null;
}

/**
 * Instance-only live context. `connected` is a REAL gateway connection and
 * only ever true on machine-instance surfaces (profile / machinery cards),
 * never on the catalog. Omit on the catalog.
 */
export interface LiveContext {
  connected?: boolean;
}

// ---------------------------------------------------------------------
//  Doc-count derivation
// ---------------------------------------------------------------------

/**
 * Canonical doc-type ↔ accepted `docTypes[]` token map (item 9). The manifest
 * `docTypes[]` uses snake/lower tokens ("spec_sheet") while the closed enum is
 * camelCase ("specSheet"). Matching is EXACT set-membership on the normalized
 * token — never substring — so "manual" never lights off "manual_addendum".
 */
const DOC_TYPE_ALIASES: Record<PopupDocType, string[]> = {
  manual: ["manual"],
  electrical: ["electrical"],
  alarms: ["alarms", "alarm"],
  specSheet: ["specsheet", "spec_sheet", "spec-sheet", "spec sheet"],
  oemNodeDocs: ["oem_node_docs", "oemnodedocs", "oem-node-docs"],
};

const normalizeDocTypeToken = (t: string): string => t.trim().toLowerCase();

/** True when `docTypes[]` contains an exact-enum token for this doc type. */
function docTypesHas(readiness: ReadinessVM | null | undefined, docType: PopupDocType): boolean {
  const types = readiness?.docTypes;
  if (!types || types.length === 0) return false;
  const aliases = DOC_TYPE_ALIASES[docType];
  return types.some((t) => aliases.includes(normalizeDocTypeToken(t)));
}

/** Total distinct source documents across all doc types. 0 when no readiness. */
export function totalDocs(readiness?: ReadinessVM | null): number {
  if (!readiness) return 0;
  const c = readiness.docCounts;
  if (c) {
    return (
      (c.manual ?? 0) +
      (c.electrical ?? 0) +
      (c.alarms ?? 0) +
      (c.specSheet ?? 0) +
      (c.oemNodeDocs ?? 0)
    );
  }
  // No per-type counts. Prefer a backend-provided total; else derive from the
  // lit doc types so the READY badge can never sit over all-dashed rows
  // (item 9): docCounts-absent readiness stays consistent with its lanes.
  const litFromTypes = (Object.keys(DOC_TYPE_ALIASES) as PopupDocType[]).filter((t) =>
    docTypesHas(readiness, t)
  ).length;
  return readiness.totalDocs ?? litFromTypes;
}

/** True when the model has at least one ingested document. */
export function hasDocs(readiness?: ReadinessVM | null): boolean {
  return totalDocs(readiness) > 0;
}

/** True when the model has at least one DIRECT (non-fanout) source. */
export function hasDirectDocs(readiness?: ReadinessVM | null): boolean {
  if (!readiness) return false;
  if (readiness.hasDirectDocs === true || readiness.hasDirectSource === true) return true;
  if (readiness.hasDirectDocs === false || readiness.hasDirectSource === false) return false;
  return (readiness.directSources?.length ?? 0) > 0;
}

// ---------------------------------------------------------------------
//  Dot states
// ---------------------------------------------------------------------

/** MIDAS dot lights whenever ANY doc is ingested (fanout-only included). */
export function midasDotLit(readiness?: ReadinessVM | null): boolean {
  return hasDocs(readiness);
}

/**
 * LIVE dot semantic state. `connected` (real gateway link) wins, then the
 * manifest's dynamic_capability. Unknown/absent capability → null. Callers
 * omit `ctx` on the catalog (connection is not knowable there).
 */
export function liveDotState(readiness?: ReadinessVM | null, ctx?: LiveContext): LiveDotState {
  if (ctx?.connected) return "connected";
  const cap = readiness?.dynamicCapability;
  if (cap === "proven") return "proven";
  if (cap === "possible") return "possible";
  return null;
}

/**
 * LIVE dot RENDERING state — the single source of truth for how the dot is
 * DRAWN on every readiness surface. Derived from `liveDotState` so semantic
 * and visual can never drift:
 *   - "lit"    : proven capability OR a real gateway connection (green dot).
 *   - "hollow" : "possible" capability, OR readiness exists but its capability
 *                is unknown/null (ring outline — "unconfirmed").
 *   - "unlit"  : no readiness at all (faint gray dot).
 * DR-3/DR-4/DR-5 MUST render the LIVE dot from THIS, not an inline mapping.
 */
export function liveDotVisual(readiness?: ReadinessVM | null, ctx?: LiveContext): LiveDotVisual {
  const state = liveDotState(readiness, ctx);
  if (state === "connected" || state === "proven") return "lit";
  if (state === "possible") return "hollow";
  // Readiness present but capability unknown → hollow (matches approved artboard).
  if (readiness) return "hollow";
  return "unlit";
}

/** OEM dot lights when the model's manufacturer (or parent) is an OEM partner. */
export function oemDotLit(model: ReadinessModel): boolean {
  return model.isOemPartner === true;
}

// ---------------------------------------------------------------------
//  Banner tier
// ---------------------------------------------------------------------

/**
 * AI-READY qualification (blue banner): DIRECT docs OR proven live
 * integration. Fanout-only models do NOT qualify.
 */
export function isAiReady(readiness?: ReadinessVM | null): boolean {
  return hasDirectDocs(readiness) || readiness?.dynamicCapability === "proven";
}

/**
 * Banner tier for a card. Max ONE banner; OEM gold OUTRANKS AI blue.
 * Returns null when neither qualifies (e.g. fanout-only or no data).
 */
export function bannerTier(model: ReadinessModel): BannerTier {
  if (model.isOemPartner === true) return "oem-gold";
  if (isAiReady(model.readiness)) return "ai-blue";
  return null;
}

// ---------------------------------------------------------------------
//  Label helpers + fanout provenance (consumed by v2-3 rows, DR-4)
// ---------------------------------------------------------------------

/**
 * Fanout-source collection → human provenance label. Extend as the bot
 * adds brand-fanout collections. Unknown collections fall back to a
 * generic control-system-brand line.
 */
export const FANOUT_PROVENANCE: Record<string, string> = {
  SINUMERIK_toc_v2: "via SINUMERIK 840D control",
};

export function fanoutProvenanceLabel(collection: string): string {
  return FANOUT_PROVENANCE[collection] ?? "via control-system brand package";
}

/**
 * Translatable word bank for `readinessSummaryLabel` / `readinessSummaryParts`.
 * Markers (★ / ●) are language-neutral and stay hardcoded in the composition;
 * only the WORDS are swapped. Callers on i18n surfaces (the machinery-card row)
 * build this from their `translate(...)`; tests + non-i18n callers get the
 * English defaults.
 *
 * NOTE (DR-3/DR-4 review fix, item 14): the availability suffix used to be a
 * single shared `available` word joined as `${telemetry} ${available}` /
 * `${oem} ${available}`. Slavic languages inflect the adjective for the noun's
 * gender ("Telemetria dostupná" vs "OEM dostupné"), which a shared word cannot
 * express. So the two "… available" cases are now FULL phrase keys
 * (`oemAvailable`, `telemetryAvailable`) the translator writes gender-correct.
 */
export interface ReadinessSummaryStrings {
  /** singular "doc" */
  doc: string;
  /** plural "docs" */
  docs: string;
  /** "OEM" bare — used only when docs are present ("2 docs · ★ OEM"). */
  oem: string;
  /** Full phrase "OEM available" — sole-OEM (no docs) case, gender-correct. */
  oemAvailable: string;
  /** Full phrase "Telemetry available" — every telemetry case, gender-correct. */
  telemetryAvailable: string;
  /** "live" — the CONNECTED (instance) phrasing */
  live: string;
}

export const EN_SUMMARY_STRINGS: ReadinessSummaryStrings = {
  doc: "doc",
  docs: "docs",
  oem: "OEM",
  oemAvailable: "OEM available",
  telemetryAvailable: "Telemetry available",
  live: "live",
};

/** The single strongest secondary signal on a readiness surface. */
export type ReadinessSignalKind = "live" | "oem" | "telemetry" | "viewDetails";

/**
 * Structured parts of the one-line readiness summary (DR-4 review fix, item 10).
 * SINGLE SOURCE for the precedence chain (connected > OEM > telemetry > docs-
 * only). BOTH `readinessSummaryLabel` (accessible name / catalog text) AND the
 * machinery-card styled composite consume THIS, so the label and the rendered
 * row can never drift out of precedence.
 */
export interface ReadinessSummaryParts {
  /** Doc-count phrase ("2 docs" / "1 doc"), or null when there are no docs. */
  docPart: string | null;
  signal: {
    kind: ReadinessSignalKind;
    /**
     * Worded signal WITHOUT the ★ / ● marker (the marker is a component in the
     * styled row, a character in the flat label). Empty string for
     * "viewDetails" (the row renders its own "View details" affordance).
     */
    text: string;
  };
}

/**
 * Compute the structured readiness summary parts. Precedence:
 * connected > OEM > telemetry > docs-only. OEM drops its "available" suffix
 * when docs are present ("2 docs · ★ OEM"); telemetry always keeps it
 * ("3 docs · Telemetry available"). No signal at all → kind "viewDetails".
 */
export function readinessSummaryParts(
  model: ReadinessModel,
  ctx?: LiveContext,
  strings: ReadinessSummaryStrings = EN_SUMMARY_STRINGS
): ReadinessSummaryParts {
  const docs = totalDocs(model.readiness);
  const isOem = model.isOemPartner === true;
  const live = liveDotState(model.readiness, ctx);
  const hasTelemetry = live === "proven" || live === "possible";
  const hasDocsPart = docs > 0;
  const docPart = hasDocsPart ? `${docs} ${docs === 1 ? strings.doc : strings.docs}` : null;

  let kind: ReadinessSignalKind = "viewDetails";
  let text = "";
  if (ctx?.connected) {
    kind = "live";
    text = strings.live;
  } else if (isOem) {
    kind = "oem";
    text = hasDocsPart ? strings.oem : strings.oemAvailable;
  } else if (hasTelemetry) {
    kind = "telemetry";
    text = strings.telemetryAvailable;
  }

  return { docPart, signal: { kind, text } };
}

/** Marker prefix for a flat (character) rendering of a signal. */
function signalMarker(kind: ReadinessSignalKind): string {
  return kind === "live" ? "● " : kind === "oem" ? "★ " : "";
}

/**
 * Compact one-line readiness summary used by the machinery-cards "Midas" row
 * (v2-3, DR-4) as the accessible name, and by the catalog surfaces as flat
 * text. Derived from `readinessSummaryParts` so it can never drift from the
 * styled row. Returns null when the model carries no signal AND no docs.
 */
export function readinessSummaryLabel(
  model: ReadinessModel,
  ctx?: LiveContext,
  strings: ReadinessSummaryStrings = EN_SUMMARY_STRINGS
): string | null {
  const { docPart, signal } = readinessSummaryParts(model, ctx, strings);
  const signalText =
    signal.kind === "viewDetails" ? null : `${signalMarker(signal.kind)}${signal.text}`;

  if (docPart) return signalText ? `${docPart} · ${signalText}` : docPart;
  return signalText;
}

// ---------------------------------------------------------------------
//  Popup doc rows (DR-3 — shared readiness popup "Machine knowledge" list)
// ---------------------------------------------------------------------

/**
 * The closed-enum doc types, in canonical display order. `oemNodeDocs` (DR-6)
 * is the fifth, OPT-IN member: see `OPT_IN_DOC_TYPES` — it is rendered only
 * when lit, so no machine gains a permanent fifth dashed lane.
 */
export type PopupDocType = "manual" | "electrical" | "alarms" | "specSheet" | "oemNodeDocs";

/**
 * Per-doc-row provenance kind for the popup:
 *  - "direct-oem"   : direct source, model IS an OEM partner → "supplied by {OEM}"
 *  - "direct-owner" : direct source, non-OEM → "uploaded by machine owner"
 *  - "fanout"       : arrived via a brand fan-out collection (e.g. SINUMERIK)
 *  - "none"         : no doc of this type ingested yet (dashed row)
 */
export type DocProvenanceKind = "direct-oem" | "direct-owner" | "fanout" | "none";

export interface PopupDocRow {
  docType: PopupDocType;
  count: number;
  lit: boolean;
  provenance: DocProvenanceKind;
  /** Fan-out collection name (only when provenance === "fanout"). */
  fanoutSource?: string;
}

const CANONICAL_DOC_ORDER: PopupDocType[] = [
  "manual",
  "electrical",
  "alarms",
  "specSheet",
  "oemNodeDocs",
];

/**
 * OPT-IN doc types (DR-6 requirement 6, LOCKED default): lanes that render
 * ONLY when their count > 0. The base four always render — dashed when not
 * ingested — but a permanent dashed "OEM node docs — none" row on every
 * machine's popup is exactly what we're avoiding, since the type applies to a
 * handful of node-scoped OEM bundles.
 *
 * SINGLE SOURCE for the rule: `popupDocRows` applies it here, and every other
 * lane renderer (readiness popup, profile card lane list, MIDAS new-chat
 * picker popover) asks `isOptInDocType` rather than re-listing doc types.
 */
const OPT_IN_DOC_TYPES: PopupDocType[] = ["oemNodeDocs"];

/** True when this doc type must be omitted entirely at count 0 (DR-6). */
export function isOptInDocType(docType: PopupDocType): boolean {
  return OPT_IN_DOC_TYPES.indexOf(docType) !== -1;
}

/**
 * Distinct source-document count for a single doc type (0 when absent).
 * When per-type `docCounts` is absent, falls back to EXACT-enum membership on
 * `docTypes[]` (item 9) — a present type counts as 1 — so the popup/profile
 * lanes light up consistently with a docCounts-less readiness instead of
 * showing a READY badge over four dashed rows.
 */
export function docCountFor(readiness: ReadinessVM | null | undefined, docType: PopupDocType): number {
  const c = readiness?.docCounts;
  if (c) return c[docType] ?? 0;
  return docTypesHas(readiness, docType) ? 1 : 0;
}

/** The lit (count > 0) doc types in canonical order — used by the profile card row. */
export function litDocTypes(readiness?: ReadinessVM | null): PopupDocType[] {
  return CANONICAL_DOC_ORDER.filter((t) => docCountFor(readiness, t) > 0);
}

/**
 * Profile-card "Midas docs" value MODE (DR-3 review fix).
 *
 * The profile card renders the lit doc TYPES ("Manual · Electrical") when they
 * are known. But a TOTAL-ONLY readiness (backend sent `totalDocs` with NO
 * per-type `docCounts` and NO `docTypes[]`) has docs (`hasDocs` true) yet zero
 * lit types → the raw `litDocTypes().map(...).join()` renders a BLANK value.
 * This resolves the mode so the caller always has something to render:
 *   - "types" : lit doc types are known → caller joins their translated labels.
 *   - "count" : docs exist but no per-type detail → caller renders "N docs".
 *   - "none"  : no docs at all → caller renders its "none yet" copy.
 * Kept in the util (not inline in the card) so the total-only fallback is
 * unit-testable without React.
 */
export type MidasDocsSummary =
  | { kind: "none" }
  | { kind: "types"; types: PopupDocType[] }
  | { kind: "count"; count: number };

export function midasDocsSummary(readiness?: ReadinessVM | null): MidasDocsSummary {
  if (!hasDocs(readiness)) return { kind: "none" };
  const types = litDocTypes(readiness);
  if (types.length > 0) return { kind: "types", types };
  return { kind: "count", count: totalDocs(readiness) };
}

/**
 * Ordered doc rows for the readiness popup. Direct docs first, then fan-out,
 * then not-ingested (dashed) — each group in canonical order. This mirrors the
 * approved Paper artboards (v2-2b/2c/2d).
 *
 * PROVENANCE ATTRIBUTION (V1 heuristic — the manifest carries only model-level
 * `directSources[]` / `brandFanoutSources[]`, NOT per-doc provenance):
 *   - `alarms` is treated as fan-out when `brandFanoutSources` is non-empty
 *     (the documented SINUMERIK-alarms fan-out reality — master spec §2/§5).
 *   - every other ingested doc type is "direct" (OEM vs owner by `isOemPartner`)
 *     — including `oemNodeDocs` (DR-6), which is direct by definition and lands
 *     as "direct-oem" on the OEM-partner models that carry it.
 * When the bot lands per-doc provenance this is the single place to refine.
 *
 * OPT-IN lanes (`isOptInDocType`, DR-6) are OMITTED entirely at count 0 — they
 * never contribute a dashed row.
 */
export function popupDocRows(model: ReadinessModel): PopupDocRow[] {
  const readiness = model.readiness;
  const isOem = model.isOemPartner === true;
  const fanoutSources = readiness?.brandFanoutSources ?? [];
  const hasFanout = fanoutSources.length > 0;

  const rows: PopupDocRow[] = CANONICAL_DOC_ORDER.map((docType): PopupDocRow | null => {
    const count = docCountFor(readiness, docType);
    if (count <= 0) {
      if (isOptInDocType(docType)) return null;
      return { docType, count: 0, lit: false, provenance: "none" as DocProvenanceKind };
    }
    if (docType === "alarms" && hasFanout) {
      return {
        docType,
        count,
        lit: true,
        provenance: "fanout" as DocProvenanceKind,
        fanoutSource: fanoutSources[0],
      };
    }
    return {
      docType,
      count,
      lit: true,
      provenance: (isOem ? "direct-oem" : "direct-owner") as DocProvenanceKind,
    };
  }).filter((row): row is PopupDocRow => row !== null);

  const rank = (p: DocProvenanceKind): number =>
    p === "direct-oem" || p === "direct-owner" ? 0 : p === "fanout" ? 1 : 2;
  // Stable sort by provenance group; canonical order preserved within a group.
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => rank(a.row.provenance) - rank(b.row.provenance) || a.i - b.i)
    .map(({ row }) => row);
}
